import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { Feature } from 'geojson'
import { useEffect } from 'react'
import { useControl } from 'react-map-gl'
import type { ControlPosition, MapRef } from 'react-map-gl'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { MISSION_MAP_DRAWING_MODES, MISSION_ZONE_TYPES } from 'utils/constants'
import { ILaunchPoint, IPoint, ITransferRoute, IZone } from 'utils/interfaces'
import { IRoute } from 'utils/interfaces'

import DirectSelectMissionPoint from './modes/DirectSelectMissionPoint'
import DirectSelectTransferRouteMode from './modes/DirectSelectTransferRouteMode'
import DirectSelectZoneMode from './modes/DirectSelectZoneMode'
import DirectSelectZoneRouteMode from './modes/DirectSelectZoneRouteMode'
import DrawPolygonMode from './modes/DrawPolygonMode'
import DrawRouteMode from './modes/DrawRouteMode'
import DrawPointMode from './modes/LaunchPointMode'
import { ViewZonesDrawMode } from './modes/ViewZonesDrawMode'

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition
  mode: MISSION_MAP_DRAWING_MODES
  tempZone: IZone | undefined
  tempZoneRoute: IRoute | undefined
  tempLaunchPoint: ILaunchPoint | undefined
  tempTransfer: ITransferRoute | undefined
  tempMissionPoint: IPoint | undefined
  onCreate: (evt: { features: Feature[] }) => void
  onUpdate: (evt: { features: Feature[]; action: string }) => void
  onDelete: (evt: { features: Feature[] }) => void
  setEditingTempLaunchPoint: () => void
  setEditingTempZone: () => void
  setEditingTempZoneRoute: () => void
}

export default function DrawControl(props: DrawControlProps) {
  const control = useControl<any>(
    () =>
      new MapboxDraw({
        ...props,
        defaultMode: 'view_zones',
        modes: Object.assign(
          {
            view_zones: ViewZonesDrawMode,
            draw_polygon: DrawPolygonMode,
            draw_route: DrawRouteMode,
            direct_select_zone: DirectSelectZoneMode,
            draw_point: DrawPointMode,
            direct_select_transfer_route: DirectSelectTransferRouteMode,
            direct_select_zone_route: DirectSelectZoneRouteMode,
            direct_select_mission_point: DirectSelectMissionPoint,
          },
          MapboxDraw.modes
        ),
      }),
    ({ map }: { map: any }) => {
      map.on('draw.create', props.onCreate)
      map.on('draw.update', props.onUpdate)
      map.on('draw.delete', props.onDelete)
      map.on('draw.route_click', (evt: any) => {
        store.dispatch(missionThunks.updateDrawnRoute(evt.feature))
      })

      const mapInstance = map.getMap()
      if (mapInstance) {
        if (!(mapInstance as any)._originalAddLayer) {
          ;(mapInstance as any)._originalAddLayer = mapInstance.addLayer.bind(mapInstance)
        }

        mapInstance.addLayer = function (layer: any, beforeId?: string) {
          try {
            if (mapInstance.getLayer(layer.id)) {
              return mapInstance
            }
          } catch (err) {}
          return (mapInstance as any)._originalAddLayer(layer, beforeId)
        }
      }

      return () => {
        if (mapInstance && (mapInstance as any)._originalAddLayer) {
          mapInstance.addLayer = (mapInstance as any)._originalAddLayer
          delete (mapInstance as any)._originalAddLayer
        }
      }
    },
    ({ map }: { map: any }) => {
      map.off('draw.create', props.onCreate)
      map.off('draw.update', props.onUpdate)
      map.off('draw.delete', props.onDelete)
      map.off('draw.route_click')
    },
    {
      position: props.position,
    }
  ) as MapboxDraw

  useEffect(() => {
    if (props.mode != MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE) {
      if (props.tempZone && props.tempZone.properties?.zone_type !== MISSION_ZONE_TYPES.RECORDED)
        if (!control.get(String(props.tempZone?.id)) && !props.tempZone.properties?.filename) {
          // the zone editing mode
          control.add(props.tempZone)
        }
    }

    if (props.tempZoneRoute && props.mode !== MISSION_MAP_DRAWING_MODES.DRAW_ROUTE) {
      if (!control.get(String(props.tempZoneRoute?.id))) {
        control.add(props.tempZoneRoute)
      }
    }

    if (props.tempLaunchPoint) {
      if (!control.get(String(props.tempLaunchPoint?.id))) {
        control.add(props.tempLaunchPoint)
      }
    } else if (props.mode !== MISSION_MAP_DRAWING_MODES.DRAW_ROUTE) {
      control.getAll().features.forEach(feature => {
        const featureInProps =
          (props.tempLaunchPoint?.id && String(props.tempLaunchPoint.id) === String(feature.id)) ||
          (props.tempZone?.id && String(props.tempZone.id) === String(feature.id)) ||
          (props.tempTransfer?.id && String(props.tempTransfer.id) === String(feature.id)) ||
          (props.tempZoneRoute?.id && String(props.tempZoneRoute.id) === String(feature.id))
        if (!featureInProps) {
          control.delete(String(feature.id))
        }
      })
    }

    if (props.tempTransfer) {
      if (!control.get(String(props.tempTransfer?.id))) {
        control.add(props.tempTransfer)
      }
    }

    if (props.tempMissionPoint) {
      if (!control.get(String(props.tempMissionPoint?.id))) {
        control.add(props.tempMissionPoint)
      }
    }
  }, [control, props.tempLaunchPoint, props.tempMissionPoint, props.tempTransfer, props.tempZone, props.tempZoneRoute])

  // Ctrl+Z / Cmd+Z undo and Ctrl+Y / Cmd+Y / Ctrl+Shift+Z / Cmd+Shift+Z redo in route/zone editing modes
  useEffect(() => {
    const isRoute = props.mode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE
    const isZone = props.mode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE
    if (!isRoute && !isZone) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) {
          if (isRoute) store.dispatch(missionThunks.redoRouteEdit())
          if (isZone) store.dispatch(missionThunks.redoZoneEdit())
        } else {
          if (isRoute) store.dispatch(missionThunks.undoRouteEdit())
          if (isZone) store.dispatch(missionThunks.undoZoneEdit())
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === 'y') {
        e.preventDefault()
        if (isRoute) store.dispatch(missionThunks.redoRouteEdit())
        if (isZone) store.dispatch(missionThunks.redoZoneEdit())
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [props.mode])

  useEffect(() => {
    const mapInstance = (control as any)._map
    if (!mapInstance) return

    // Remove all listeners first
    mapInstance.off('click', 'gl-draw-launch-point.cold', props.setEditingTempLaunchPoint as any)
    mapInstance.off('click', 'gl-draw-launch-point-center.cold', props.setEditingTempLaunchPoint as any)
    mapInstance.off('click', 'gl-draw-polygon-fill.cold', props.setEditingTempZone as any)
    mapInstance.off('click', 'gl-draw-line-zone-route.cold', props.setEditingTempZoneRoute as any)

    if (props.mode !== MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE) {
      mapInstance.on('click', 'gl-draw-launch-point.cold', props.setEditingTempLaunchPoint as any)
      mapInstance.on('click', 'gl-draw-launch-point-center.cold', props.setEditingTempLaunchPoint as any)
      mapInstance.on('click', 'gl-draw-polygon-fill.cold', props.setEditingTempZone as any)
    }

    return () => {
      if (mapInstance) {
        mapInstance.off('click', 'gl-draw-launch-point.cold', props.setEditingTempLaunchPoint as any)
        mapInstance.off('click', 'gl-draw-launch-point-center.cold', props.setEditingTempLaunchPoint as any)
        mapInstance.off('click', 'gl-draw-polygon-fill.cold', props.setEditingTempZone as any)
        mapInstance.off('click', 'gl-draw-line-zone-route.cold', props.setEditingTempZoneRoute as any)
      }
    }
  }, [control, props.mode, props.setEditingTempLaunchPoint, props.setEditingTempZone, props.setEditingTempZoneRoute])

  useEffect(() => {
    switch (props.mode) {
      case MISSION_MAP_DRAWING_MODES.VIEW_ZONES:
        control.deleteAll()
        control.changeMode('view_zones')
        break
      case MISSION_MAP_DRAWING_MODES.DRAW_POLYGON:
        control.changeMode('draw_polygon')
        break
      case MISSION_MAP_DRAWING_MODES.DRAW_ROUTE:
        control.changeMode('draw_route')
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE:
        if (props.tempZone && props.tempZone.id) {
          if (!control.get(String(props.tempZone.id))) {
            control.add(props.tempZone)
          }
          control.changeMode('direct_select_zone', { featureId: String(props.tempZone.id) })
        }
        break
      case MISSION_MAP_DRAWING_MODES.ADD_ZONE_LAUNCH_POINT:
        control.changeMode('draw_point')
        break
      case MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT:
      case MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT:
        control.changeMode('draw_point')
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE:
        if (props.tempTransfer && props.tempTransfer.id) {
          if (!control.get(String(props.tempTransfer.id))) {
            control.add(props.tempTransfer)
          }
          control.changeMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE, {
            featureId: String(props.tempTransfer.id),
          })
        }
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE:
        if (props.tempZoneRoute && props.tempZoneRoute.id) {
          // Re-add the feature if it was cleared (e.g. after VIEW_ZONES deleteAll during undo)
          if (!control.get(String(props.tempZoneRoute.id))) {
            control.add(props.tempZoneRoute)
          }
          control.changeMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE, {
            featureId: String(props.tempZoneRoute.id),
          })
        }
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT:
        if (props.tempMissionPoint && props.tempMissionPoint.id) {
          control.changeMode('direct_select_mission_point', {
            featureIds: [String(props.tempMissionPoint.id)],
          })
        }
        break
      case MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_LAUNCH_POINT:
        if (props.tempLaunchPoint && props.tempLaunchPoint.id) {
          control.changeMode('direct_select_mission_point', {
            featureIds: [String(props.tempLaunchPoint.id)],
          })
        }
        break
      default:
        control.changeMode('view_zones')
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode])

  return null
}
